// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { ChevronDown, ChevronRight, FileText, Briefcase } from 'lucide-react';

// Deployment 17 — P2 — Guida KID
// Pagina educativa con 8 sezioni espandibili, struttura identica alla guida busta paga.
// Nessun calcolo interattivo, contenuto statico in italiano.

const SECTIONS = [
  {
    id: 'intro',
    title: '1 — Cos\'è il KID/KIID',
    body: (
      <>
        <p>Il <strong>KID (Key Information Document)</strong> è il documento informativo standardizzato che ogni distributore di prodotti finanziari nell'Unione Europea è obbligato per legge a consegnarti prima di vendere un fondo, un ETF o un certificato.</p>
        <p>Esistono <strong>due formati</strong> ancora in circolazione:</p>
        <ul>
          <li><strong>KIID</strong> (Key Investor Information Document) — vecchio formato UCITS, 2 pagine. Usato fino al 2022 per fondi e alcuni ETF.</li>
          <li><strong>KID PRIIPs</strong> (Packaged Retail and Insurance-based Investment Products) — formato in vigore dal 2023, 3 pagine. Tutti gli ETF UCITS distribuiti in UE devono usare questo formato dal 1° gennaio 2023.</li>
        </ul>
        <p>Esempio pratico: se cerchi il KID di un ETF lanciato nel 2010, potresti trovare il vecchio KIID nei documenti storici e il nuovo KID PRIIPs nella versione corrente. Usa sempre quello più recente.</p>
      </>
    ),
  },
  {
    id: 'cos-e-prodotto',
    title: '2 — "Cos\'è questo prodotto"',
    body: (
      <>
        <p>La prima sezione di un KID descrive il prodotto in termini semplici. Cerca queste informazioni:</p>
        <ul>
          <li><strong>Tipo di strumento:</strong> ETF UCITS, fondo aperto OICVM, fondo pensione, certificato strutturato.</li>
          <li><strong>Obiettivo di investimento:</strong> ad esempio "Replicare l'andamento dell'indice MSCI World Net Total Return".</li>
          <li><strong>Orizzonte temporale consigliato:</strong> il periodo minimo per cui dovresti detenere lo strumento.</li>
          <li><strong>Target investor:</strong> chi è il cliente tipo per questo prodotto.</li>
        </ul>
        <p>"<strong>OICVM</strong>" (Organismo di Investimento Collettivo in Valori Mobiliari) e "<strong>UCITS</strong>" (Undertakings for Collective Investment in Transferable Securities) sono sinonimi. Indicano fondi armonizzati a livello europeo, soggetti a regole stringenti di diversificazione e protezione dell'investitore retail.</p>
      </>
    ),
  },
  {
    id: 'rischio',
    title: '3 — Rischio e rendimento (scala 1-7)',
    body: (
      <>
        <p>Il KID PRIIPs include un <strong>indicatore sintetico di rischio</strong> in scala da 1 a 7:</p>
        <ul>
          <li><strong>1-2:</strong> rischio molto basso (es. fondi monetari, BTP a brevissima scadenza).</li>
          <li><strong>3-4:</strong> rischio medio (es. obbligazionari diversificati).</li>
          <li><strong>5-6:</strong> rischio alto (es. ETF azionari globali).</li>
          <li><strong>7:</strong> rischio molto alto (es. azioni di mercati emergenti, crypto, prodotti a leva).</li>
        </ul>
        <p>Il livello di rischio <strong>può cambiare nel tempo</strong> — viene ricalcolato annualmente sulla base della volatilità storica.</p>
        <p>Distingui tra <strong>rischio di mercato</strong> (oscillazioni del prezzo) e <strong>rischio di credito</strong> (insolvenza dell'emittente). Il KID copre entrambi ma in sezioni diverse.</p>
      </>
    ),
  },
  {
    id: 'scenari',
    title: '4 — Scenari di performance',
    body: (
      <>
        <p>Il KID mostra <strong>quattro scenari</strong> proiettati sull'orizzonte raccomandato (di solito 5 anni):</p>
        <ul>
          <li><strong>Stress:</strong> condizioni di mercato estreme (es. crisi 2008).</li>
          <li><strong>Sfavorevole:</strong> 10° percentile inferiore della distribuzione storica.</li>
          <li><strong>Moderato:</strong> mediana storica.</li>
          <li><strong>Favorevole:</strong> 90° percentile superiore.</li>
        </ul>
        <p><strong>Attenzione:</strong> lo scenario moderato <strong>non è "il più probabile"</strong> — è la mediana di una distribuzione. La realtà può essere significativamente diversa. Le performance passate non garantiscono risultati futuri.</p>
      </>
    ),
  },
  {
    id: 'costi',
    title: '5 — Costi (TER e altri)',
    body: (
      <>
        <p>La sezione costi è la più importante per un investitore passivo. Il KID elenca:</p>
        <ul>
          <li><strong>TER (Total Expense Ratio):</strong> costi annui ricorrenti di gestione, custodia, audit. Per un ETF passivo di solito tra 0,05% e 0,30% l'anno.</li>
          <li><strong>Costi di entrata/uscita:</strong> di solito zero per ETF in borsa, possono essere 1-3% per fondi attivi.</li>
          <li><strong>Costi di transazione:</strong> spread bid-ask interno e commissioni di compravendita dei sottostanti.</li>
        </ul>
        <p><strong>Esempio pratico:</strong> investi €10.000 per 20 anni in un ETF a TER 0,20% (totale costo ~€400) o in un fondo attivo a TER 2,00% (totale costo ~€4.000+). La differenza in 20 anni può superare i €15.000 a parità di rendimento lordo. <Link to="/calcolatori/pac" className="text-[#C9A84C] hover:underline">Verifica nel calcolatore PAC →</Link></p>
      </>
    ),
  },
  {
    id: 'detenzione',
    title: '6 — Periodo di detenzione raccomandato',
    body: (
      <>
        <p>Il <strong>periodo di detenzione raccomandato</strong> è il tempo minimo per cui dovresti tenere lo strumento per ridurre il rischio di perdite e ammortizzare i costi.</p>
        <p>Per un ETF azionario è tipicamente <strong>5-7 anni</strong>, per un obbligazionario <strong>3-5 anni</strong>, per un fondo pensione l'orizzonte previdenziale (anche decenni).</p>
        <p><strong>Cosa succede se vendi prima:</strong> perdita probabile a causa della volatilità, costi non ammortizzati, eventuali tasse anticipate.</p>
        <p>Differenza tra <strong>periodo raccomandato</strong> (regola di prudenza dell'emittente) e <strong>periodo minimo</strong> (sotto questa soglia il prodotto è oggettivamente inadatto).</p>
      </>
    ),
  },
  {
    id: 'come-trovare',
    title: '7 — Come trovare il KID del tuo ETF',
    body: (
      <>
        <p>Tre modi affidabili per trovare il KID PRIIPs aggiornato:</p>
        <ul>
          <li>
            <strong>Su Borsa Italiana</strong> → <a className="text-[#C9A84C] hover:underline" href="https://www.borsaitaliana.it" target="_blank" rel="noreferrer">borsaitaliana.it</a> → cerca per ISIN dello strumento → sezione "Documenti" → KIID/KID.
          </li>
          <li>
            <strong>Nel simulatore SAPIENS:</strong> ogni strumento ETF ha il link diretto al KID dal pannello informazioni della tabella portafoglio. <Link to="/strumenti/simulatore-portafoglio" className="text-[#C9A84C] hover:underline">→ Apri il Simulatore Portafoglio</Link>
          </li>
          <li>
            <strong>Sul sito dell'emittente:</strong> BlackRock (<a className="text-[#C9A84C] hover:underline" href="https://www.blackrock.com/it" target="_blank" rel="noreferrer">blackrock.com/it</a>), Vanguard (<a className="text-[#C9A84C] hover:underline" href="https://www.it.vanguard" target="_blank" rel="noreferrer">it.vanguard</a>), Amundi (<a className="text-[#C9A84C] hover:underline" href="https://www.amundietf.it" target="_blank" rel="noreferrer">amundietf.it</a>), Lyxor, Invesco, DWS, Xtrackers.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: 'esempio',
    title: '8 — Esempio pratico: leggiamo un KID insieme',
    body: (
      <>
        <p>Ecco un KID PRIIPs di esempio (dati inventati a scopo educativo) — leggiamolo campo per campo.</p>
        <div className="my-4 rounded-xl border border-[#C9A84C]/30 bg-[#252523] p-4 space-y-2 text-sm" data-testid="kid-example-card">
          <div><strong className="text-[#C9A84C]">Nome:</strong> SAPIENS Esempio ETF UCITS</div>
          <div><strong className="text-[#C9A84C]">ISIN:</strong> IE00ESEMPIO0001</div>
          <div><strong className="text-[#C9A84C]">Emittente:</strong> SAPIENS Asset Management (Esempio educativo)</div>
          <div><strong className="text-[#C9A84C]">Indicatore di rischio:</strong> 4/7 (rischio medio)</div>
          <div><strong className="text-[#C9A84C]">Scenario moderato 5 anni:</strong> +12% (mediana storica)</div>
          <div><strong className="text-[#C9A84C]">TER:</strong> 0,20%/anno</div>
          <div><strong className="text-[#C9A84C]">Periodo di detenzione raccomandato:</strong> 5 anni</div>
        </div>
        <p>Cosa significano questi numeri:</p>
        <ul>
          <li>Rischio 4/7 → adatto a chi ha già esperienza con i mercati e accetta perdite temporanee.</li>
          <li>Scenario moderato +12% in 5 anni → ~2,3% l'anno annualizzato (mediana, non garanzia).</li>
          <li>TER 0,20% → stai pagando €20 l'anno ogni €10.000 investiti, in linea con i migliori ETF passivi.</li>
          <li>Detenzione 5 anni → vendere prima del 2031 espone a maggior rischio di perdita.</li>
        </ul>
      </>
    ),
  },
];

const SectionItem = ({ section, isOpen, onToggle }) => (
  <div className="border-b border-white/[0.06]" data-testid={`kid-section-${section.id}`}>
    <button
      type="button"
      onClick={onToggle}
      className="w-full flex items-center justify-between text-left px-4 py-4 hover:bg-white/[0.02] transition-colors"
      aria-expanded={isOpen}
      data-testid={`kid-section-toggle-${section.id}`}
    >
      <span className="text-sm font-medium text-[#E8E8E4]">{section.title}</span>
      {isOpen ? <ChevronDown className="w-4 h-4 text-[#C9A84C]" /> : <ChevronRight className="w-4 h-4 text-[#888780]" />}
    </button>
    {isOpen && (
      <div className="px-4 pb-5 text-sm text-[#888780] leading-relaxed space-y-3" data-testid={`kid-section-body-${section.id}`}>
        {section.body}
      </div>
    )}
  </div>
);

const GuidaKID = () => {
  const [openId, setOpenId] = useState('intro');
  const toggle = (id) => setOpenId(openId === id ? null : id);

  return (
    <div className="min-h-screen bg-[#1C1C1A]" data-testid="guida-kid-page">
      <Helmet>
        <title>Come leggere un KID — Guida completa per investitori | SAPIENS</title>
        <meta name="description" content="Tutto quello che devi sapere per leggere un KID PRIIPs prima di comprare un ETF o un fondo: rischio, scenari, costi, periodo di detenzione." />
        <link rel="canonical" href="https://finsapiens.it/guida-kid" />
      </Helmet>
      <div className="container-wide py-10 max-w-3xl">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="w-5 h-5 text-[#C9A84C]" />
            <span className="text-xs text-[#888780] uppercase tracking-wider">Guida educativa</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-medium text-[#E8E8E4] mb-3">
            Come leggere un KID
          </h1>
          <p className="text-[#888780]" style={{ fontSize: 15, lineHeight: 1.7 }}>
            Il documento che ogni investitore deve leggere prima di comprare un ETF o un fondo.
          </p>
        </motion.div>

        {/* Disclaimer */}
        <div className="sapiens-note-box text-sm mb-6" data-testid="kid-disclaimer">
          Le spiegazioni presenti in questa guida hanno scopo esclusivamente educativo. Il KID/KIID ufficiale è il documento legale vincolante — in caso di discrepanza, fare sempre riferimento al documento originale dell'emittente.
        </div>

        {/* Cross-link: Simulatore */}
        <Link to="/strumenti/simulatore-portafoglio" data-testid="kid-link-simulatore"
          className="block mb-6 p-4 rounded-xl transition-colors hover:bg-white/[0.04]"
          style={{ background: '#252523', border: '1px solid rgba(61,122,82,0.3)' }}>
          <div className="flex items-center gap-3">
            <Briefcase className="w-5 h-5 text-[#3D7A52] flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-[#E8E8E4] text-sm">Apri il Simulatore Portafoglio</p>
              <p className="text-xs text-[#888780] mt-0.5">Per ogni ETF nel simulatore SAPIENS è disponibile il link diretto al KID dal pannello informazioni dello strumento.</p>
            </div>
          </div>
        </Link>

        {/* 8 sezioni */}
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
          className="rounded-2xl overflow-hidden mb-6 bg-[#252523]"
          style={{ border: '1px solid rgba(255,255,255,0.1)' }}
          data-testid="kid-sections"
        >
          {SECTIONS.map((s) => (
            <SectionItem key={s.id} section={s} isOpen={openId === s.id} onToggle={() => toggle(s.id)} />
          ))}
        </motion.div>
      </div>
    </div>
  );
};

export default GuidaKID;
